import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, CircularProgress, List, ListItemButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import PageContainer from '../PageContainer';
import NotificationActorRow from './NotificationActorRow';
import { formatInboxTimeLabel } from './notificationTimeFormat';
import { usePortalNotificationsInfiniteQuery } from '../../api/use-portal-notifications-query';
import { getPortalNotificationListItemKey } from '../../utils/mapPortalNotification';
import { useNotificationContext } from '../../contexts/NotificationProvider';
import toast from 'react-hot-toast';
import NotificationAlertsSetupPrompt from '../NotificationAlertsSetupPrompt';
import PortalEmptyState from '../layout/PortalEmptyState';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { PORTAL_ACCENT } from '../../styles/portalTheme';

const PortalNotificationsPage = ({ title = 'Notifications' }) => {
  const loadMoreRef = useRef(null);
  const { openNotification, markAllRead } = useNotificationContext() ?? {};

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePortalNotificationsInfiniteQuery();

  const notifications = useMemo(
    () => data?.pages.flatMap((page) => page.notifications) ?? [],
    [data],
  );

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.read).length,
    [notifications],
  );

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root: null, rootMargin: '240px', threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, notifications.length]);

  const handleOpen = (item) => {
    void openNotification?.(item);
  };

  const handleMarkAllRead = () => {
    markAllRead?.mutate(undefined, {
      onSuccess: () => toast.success('All notifications marked as read'),
      onError: (err) => toast.error(err?.message || 'Could not mark all as read'),
    });
  };

  return (
    <PageContainer title={title}>
      <NotificationAlertsSetupPrompt />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          {notifications.length} notification{notifications.length === 1 ? '' : 's'}
          {unreadCount > 0 ? ` · ${unreadCount} unread` : ''}
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={handleMarkAllRead}
          disabled={markAllRead?.isPending || unreadCount === 0 || notifications.length === 0}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            minWidth: 'auto',
            py: 0.25,
          }}
        >
          {markAllRead?.isPending ? 'Updating…' : 'Mark all read'}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} color="primary" />
        </Box>
      ) : null}

      {isError ? (
        <Typography variant="body2" color="error" sx={{ mb: 1.5, fontSize: '0.875rem' }}>
          {error?.message || 'Could not load notifications.'}
        </Typography>
      ) : null}

      {!isLoading && !isError && notifications.length === 0 ? (
        <PortalEmptyState
          icon={NotificationsNoneOutlinedIcon}
          title="No notifications yet"
          description="When something needs your attention, it will show up here."
          compact
        />
      ) : null}

      {!isLoading && notifications.length > 0 ? (
        <List
          disablePadding
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1.5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {notifications.map((item, index) => {
            const isUnread = !item.read;
            const accentColor = item.meta?.accentColor ?? PORTAL_ACCENT;

            return (
              <ListItemButton
                key={getPortalNotificationListItemKey(item, index)}
                onClick={() => handleOpen(item)}
                divider={index < notifications.length - 1}
                sx={{
                  py: 0.875,
                  px: 1.25,
                  alignItems: 'center',
                  bgcolor: isUnread ? alpha(accentColor, 0.04) : 'background.paper',
                  '&:hover': {
                    bgcolor: isUnread ? alpha(accentColor, 0.08) : 'action.hover',
                  },
                }}
              >
                <NotificationActorRow
                  actorFullName={item.actorFullName}
                  actorAvatarUrl={item.actorAvatarUrl}
                  title={item.title}
                  timeLabel={formatInboxTimeLabel(item)}
                  accentColor={accentColor}
                  isUnread={isUnread}
                />
              </ListItemButton>
            );
          })}
        </List>
      ) : null}

      <Box
        ref={loadMoreRef}
        sx={{ py: 1.5, display: 'flex', justifyContent: 'center', minHeight: 32 }}
      >
        {isFetchingNextPage ? <CircularProgress size={20} color="primary" /> : null}
        {!isFetchingNextPage && hasNextPage ? (
          <Typography variant="caption" color="text.secondary">
            Scroll for more…
          </Typography>
        ) : null}
        {!hasNextPage && notifications.length > 0 && !isLoading ? (
          <Typography variant="caption" color="text.secondary">
            End of list
          </Typography>
        ) : null}
      </Box>

      {isFetching && !isLoading && !isFetchingNextPage ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Refreshing…
        </Typography>
      ) : null}

      {isError ? (
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={() => refetch()}
          sx={{
            mt: 1.5,
            border: 'none',
            bgcolor: 'transparent',
            color: 'primary.main',
            cursor: 'pointer',
            p: 0,
            fontSize: '0.875rem',
            textDecoration: 'underline',
          }}
        >
          Try again
        </Typography>
      ) : null}
    </PageContainer>
  );
};

export default PortalNotificationsPage;
